import { Link } from 'react-router-dom';

import { usePatient } from '../schedule/usePatient.ts';

/**
 * A patient's name via `usePatient` (`schedule/usePatient.ts`), as `Lastname, Firstname` — the
 * shared version of a three-line shape that already exists twice, independently, in this app:
 * `schedule/AppointmentList.tsx`'s file-local `PatientCell` and `schedule/WaitlistPanel.tsx`'s own
 * `PatientName` (that file's own header names `PatientCell` as the reason it wrote a *second* copy
 * rather than importing one, since `claude-kit/rules/invariants.md`'s "favor the general, reusable
 * solution once a pattern actually recurs" reserves extraction for a third occurrence). This module's
 * two call sites (`inbox/ThreadList.tsx`, `inbox/ThreadDetail.tsx`) are that third and fourth
 * occurrence, so this is the extraction rather than a fifth copy.
 *
 * **`AppointmentList.tsx`'s and `WaitlistPanel.tsx`'s own inline copies are left as-is, not
 * retrofitted to import this** — matching `components/QuerySection.tsx`'s identical precedent for
 * `PatientDermatologyPage.tsx`'s own inline version: touching either file is out of scope for the
 * task this component was built for (`BACKLOG.md` W2's staff inbox), and neither is a build-time
 * requirement of this one.
 *
 * Loading/unknown copy is passed in rather than looked up here, since each caller's own translation
 * namespace differs (`schedule`, `inbox`, ...) — this component renders presentation, not policy
 * about which namespace owns the words.
 */
export function PatientLabel({
  patientId,
  loadingLabel,
  unknownLabel,
  linkToChart = false,
}: {
  readonly patientId: string;
  readonly loadingLabel: string;
  readonly unknownLabel: string;
  /** Render the name as a link to `/patients/:patientId` rather than plain text — the chart-open
   * case (`ThreadDetail.tsx`), not the compact list-row case (`ThreadList.tsx`). */
  readonly linkToChart?: boolean;
}) {
  const patient = usePatient(patientId);

  if (patient.data === undefined) {
    return <span className="text-ink-muted">{patient.isError ? unknownLabel : loadingLabel}</span>;
  }

  const name = `${patient.data.lastName}, ${patient.data.firstName}`;
  if (!linkToChart) {
    return (
      <span className="font-medium text-ink break-words [overflow-wrap:anywhere]">{name}</span>
    );
  }
  return (
    <Link
      to={`/patients/${patientId}`}
      className="font-medium text-accent-text break-words underline-offset-2 [overflow-wrap:anywhere] hover:underline"
    >
      {name}
    </Link>
  );
}
